import { MetaMorphoAbi } from '@1delta/abis'
import {
  morphoPools,
  morphoTypeVaults,
  type MorphoTypeVaultEntry,
} from '@1delta/data-sdk'
import { formatEther, parseAbi, parseUnits } from 'viem'
import lodash from 'lodash'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import { GenericTokenList, MulticallRetryFunction } from '../../types'
import { parseRawAmount } from '../../utils/parsing'
import { composeVaultDisplayName } from '../displayName'
import { MorphoVault, MorphoVaults } from './types'
import { MORPHO_LENS } from '../../lending/user-data/morpho/userCallBuild'
import {
  decodeMarkets,
  type Market,
} from '../../lending/public-data/morpho/utils/evmParser'
import { MathLib } from '../../lending/public-data/morpho/utils/mathLib'
import { apyToApr } from '../../lending/user-data/utils'
import {
  MarketRate,
  computeVaultAllocation,
  computeV2Allocation,
  fetchVaultSupplyShares,
} from './allocationWalk'
import { fetchAssetDecimals } from '../assetDecimals'
import { deriveConvertToAssets } from '../sharePrice'
import { isMorphoStubVault } from './stubVaults'

const { chunk } = lodash

/** Methods called per vault — order matters: index → field. */
const VAULT_CALLS = [
  'name',
  'symbol',
  'decimals',
  'totalAssets',
  'totalSupply',
  'fee',
  'timelock',
  'owner',
  'curator',
  'guardian',
] as const
const CALLS_PER_VAULT = VAULT_CALLS.length

/**
 * Phase-1 calls per vault — `VAULT_CALLS` plus `withdrawQueueLength` appended
 * last (index {@link WITHDRAW_QUEUE_LENGTH_INDEX}). `parseVault` only consumes
 * the first {@link CALLS_PER_VAULT}; the queue length drives the phase-2
 * allocation walk that yields the supply APR.
 */
const PHASE1_CALLS = [
  ...VAULT_CALLS,
  'withdrawQueueLength', // V1 (MetaMorpho) — reverts on V2
  'adaptersLength', // V2 (Vaults V2) — reverts on V1
  'performanceFee', // V2 fee getter (V1 uses `fee()`)
] as const
const PHASE1_PER_VAULT = PHASE1_CALLS.length
const WITHDRAW_QUEUE_LENGTH_INDEX = CALLS_PER_VAULT
const ADAPTERS_LENGTH_INDEX = CALLS_PER_VAULT + 1
const PERFORMANCE_FEE_INDEX = CALLS_PER_VAULT + 2

/** Morpho lens read surface — same packed-bytes shape the markets path uses. */
const MORPHO_LENS_ABI = parseAbi([
  'function getMarketDataCompact(address morpho, bytes32[] calldata marketsIds) external view returns (bytes memory data)',
])
const MARKET_CHUNK = 100

/**
 * Morpho **Vaults V2** surface. V2 vaults are adapter-based (no `withdrawQueue`):
 * each `adapters(i)` is a MorphoMarketV1Adapter that allocates into Morpho Blue
 * markets, enumerable via `marketIds(j)` with per-market assets from
 * `expectedSupplyAssets(id)`. `adaptersLength()`/`performanceFee()` are read in
 * phase 1 to detect V2 and get its fee.
 */
const VAULT_V2_PHASE1_ABI = parseAbi([
  'function adaptersLength() view returns (uint256)',
  'function performanceFee() view returns (uint96)',
])
const VAULT_V2_ABI = parseAbi([
  'function adapters(uint256) view returns (address)',
])
const VAULT_V2_ADAPTER_ABI = parseAbi([
  'function marketIdsLength() view returns (uint256)',
  'function marketIds(uint256) view returns (bytes32)',
  'function expectedSupplyAssets(bytes32) view returns (uint256)',
])

/** Combined phase-1 ABI — MetaMorpho (V1) getters + the two V2 detection
 *  getters. `multicallRetry` decodes each call by function name, so a vault
 *  that lacks a given getter simply fails that slot under `allowFailure`. */
const PHASE1_ABI = [...(MetaMorphoAbi as any), ...VAULT_V2_PHASE1_ABI]

/** MetaMorpho `fee()` is 1e18-scaled (e.g. `0.05e18` = 5 %). */
const FEE_SCALE = 1e18

const isHex40 = (addr: unknown): addr is string =>
  typeof addr === 'string' && /^0x[0-9a-f]{40}$/i.test(addr)

const isHex64 = (v: unknown): v is string =>
  typeof v === 'string' && /^0x[0-9a-f]{64}$/i.test(v)

const lcOrUndefined = (addr: unknown): string | undefined =>
  isHex40(addr) ? (addr as string).toLowerCase() : undefined

const toNumberSafe = (raw: unknown, fallback = 0): number => {
  if (raw === undefined || raw === null) return fallback
  const n = Number(raw)
  return Number.isFinite(n) ? n : fallback
}

const toStringSafe = (raw: unknown, fallback = ''): string => {
  if (raw === undefined || raw === null) return fallback
  return String(raw)
}

/** Coerce a multicall cell to bigint; `undefined` on failure/non-numeric. */
const toBigIntSafe = (raw: unknown): bigint | undefined => {
  if (raw === undefined || raw === null) return undefined
  try {
    if (typeof raw === 'bigint') return raw
    if (typeof raw === 'number') return BigInt(Math.trunc(raw))
    if (typeof raw === 'string' && /^\d+$/.test(raw)) return BigInt(raw)
    return undefined
  } catch {
    return undefined
  }
}

/** Formatted underlying amount → raw integer string (inverse of
 *  `parseRawAmount`). Returns '0' on malformed input. */
const toRawAmount = (formatted: number, decimals: number): string => {
  try {
    return parseUnits(formatted.toFixed(decimals), decimals).toString()
  } catch {
    return '0'
  }
}

/**
 * Lista DAO vaults are Moolah-fork contracts — diverge from MetaMorpho on
 * governance (`AccessControlEnumerable` instead of `owner()/curator()/
 * guardian()/timelock()`), so they have a dedicated path
 * (`fetchListaVaultsFromChain`). Skip them in the standard MetaMorpho path
 * by default to avoid populating partial entries.
 */
const DEFAULT_SKIP_PROTOCOLS = new Set(['LISTA_DAO'])

const collectEntries = (
  chainId: string,
  protocols?: string[],
  skip?: ReadonlySet<string>,
): Array<{ protocol: string; entry: MorphoTypeVaultEntry }> => {
  const all = morphoTypeVaults() ?? {}
  const out: Array<{ protocol: string; entry: MorphoTypeVaultEntry }> = []
  const seen = new Set<string>()
  const protocolKeys =
    protocols && protocols.length > 0 ? protocols : Object.keys(all)
  for (const protocol of protocolKeys) {
    if (skip?.has(protocol)) continue
    const list = all[protocol]?.[chainId] ?? []
    for (const entry of list) {
      const vaultAddr = entry?.vault?.toLowerCase()
      if (!vaultAddr || seen.has(vaultAddr)) continue
      seen.add(vaultAddr)
      out.push({ protocol, entry })
    }
  }
  return out
}

export interface FetchMorphoVaultsFromChainOptions {
  /** Restrict to a subset of curator-protocol keys (e.g. `['LISTA_DAO']`). */
  protocols?: string[]
  /**
   * Override the registry — `[{ vault, underlying, name? }]`. Lets fork tests
   * point at a specific vault list without populating the global registry.
   */
  vaultsOverride?: MorphoTypeVaultEntry[]
}

/**
 * On-chain MetaMorpho vault fetcher — multicalls every vault listed in
 * `morphoTypeVaults()` for the chain (or in `opts.vaultsOverride`) and
 * normalizes the results to the same `MorphoVaults` shape produced by the
 * Morpho API and Goldsky paths.
 *
 * Intended as a fallback for forks / chains where the Morpho public API and
 * Goldsky subgraph are unreachable. The supply rate isn't computable from
 * vault state alone (it requires walking the underlying market allocations),
 * so `supplyRate` and `rewardsRate` are surfaced as `0` — same convention
 * used by the Goldsky path when rewards aren't indexed. `liquidity` is the
 * real withdrawable amount from the allocation walk (idle + Σ min(allocation,
 * market liquidity)); it falls back to `totalAssets` only when no lens/core is
 * available for the chain.
 */
export const fetchMorphoVaultsFromChain = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  opts: FetchMorphoVaultsFromChainOptions = {},
): Promise<MorphoVaults> => {
  const entries = (
    opts.vaultsOverride
      ? opts.vaultsOverride.map((entry) => ({ protocol: '', entry }))
      : collectEntries(chainId, opts.protocols, DEFAULT_SKIP_PROTOCOLS)
  ).filter(({ entry }) => {
    // The factory deployment smoke-test (see stubVaults.ts) — refused by
    // address whatever the registry says.
    if (!isMorphoStubVault(chainId, entry.vault)) return true
    console.warn(
      `[morpho vaults] chain ${chainId}: skipping ${entry.vault} — factory smoke-test vault (stub underlying)`,
    )
    return false
  })
  if (entries.length === 0) return {}

  // Phase 1: per-vault static fields + withdraw-queue length, one batch.
  const phase1Calls = entries.flatMap(({ entry }) =>
    PHASE1_CALLS.map((name) => ({
      address: entry.vault,
      name,
      params: [],
    })),
  )
  const phase1 = await multicallRetry({
    chain: chainId,
    calls: phase1Calls as any,
    abi: phase1Calls.map(() => PHASE1_ABI) as any,
    allowFailure: true,
  })

  // V1 (MetaMorpho) vs V2 (Vaults V2) per entry. Prefer the registry's
  // `version` tag when present; else auto-detect — V2 vaults expose
  // `adaptersLength()` and revert on `withdrawQueueLength()`.
  const adapterLens = entries.map((_, i) =>
    Number(phase1[i * PHASE1_PER_VAULT + ADAPTERS_LENGTH_INDEX]),
  )
  const isV2 = entries.map(({ entry }, i) => {
    if (entry.version === 'v2') return true
    if (entry.version === 'v1') return false
    return Number.isFinite(adapterLens[i])
  })

  const queueLens = entries.map((_, i) => {
    if (isV2[i]) return 0 // V2 has no withdraw queue
    const n = Number(phase1[i * PHASE1_PER_VAULT + WITHDRAW_QUEUE_LENGTH_INDEX])
    return Number.isFinite(n) && n > 0 ? n : 0
  })

  // Phase 2: enumerate each vault's withdraw queue → the markets it lends to.
  const phase2Calls: { address: string; name: string; params: any[] }[] = []
  const phase2Vault: number[] = []
  entries.forEach(({ entry }, i) => {
    for (let q = 0; q < queueLens[i]; q++) {
      phase2Calls.push({
        address: entry.vault,
        name: 'withdrawQueue',
        params: [BigInt(q)],
      })
      phase2Vault.push(i)
    }
  })
  const phase2 = phase2Calls.length
    ? await multicallRetry({
        chain: chainId,
        calls: phase2Calls as any,
        abi: phase2Calls.map(() => MetaMorphoAbi) as any,
        allowFailure: true,
      })
    : []
  const marketIdsByVault: string[][] = entries.map(() => [])
  phase2.forEach((res, k) => {
    if (isHex64(res)) marketIdsByVault[phase2Vault[k]].push(res.toLowerCase())
  })

  // Phase 3 (parallel): per-market supply rates from the lens, and each
  // vault's supply shares per market from the Morpho Blue core. Both feed the
  // allocation-weighting in `parseVault`. Degrades to APR 0 when the lens or
  // core is unavailable for the chain.
  const core = morphoPools()?.MORPHO_BLUE?.[chainId]
  const allMarketIds = [...new Set(marketIdsByVault.flat())]
  const underlyings = [
    ...new Set(
      entries
        .map(({ entry }) => entry.underlying?.toLowerCase())
        .filter((a): a is string => !!a),
    ),
  ]
  const [rateMap, positionMap, assetDecimals] = await Promise.all([
    fetchMorphoMarketRates(chainId, core, allMarketIds, multicallRetry),
    fetchVaultSupplyShares(
      chainId,
      core,
      entries.map((e) => e.entry),
      marketIdsByVault,
      multicallRetry,
    ),
    fetchAssetDecimals(chainId, underlyings, multicallRetry),
  ])

  // Resolve the decimals used to format a vault's `totalAssets` — the asset's
  // own decimals (NOT the share `decimals()`, often 18 via the offset).
  const resolveDecimals = (i: number): number => {
    const { entry } = entries[i]
    const underlying = entry.underlying?.toLowerCase() ?? ''
    const decimalsRaw = phase1[i * PHASE1_PER_VAULT + 2]
    return (
      assetDecimals.get(underlying) ??
      tokenList[underlying]?.decimals ??
      Number(decimalsRaw ?? 18)
    )
  }

  // V2 (Vaults V2) allocation walk — adapters → Morpho-market assets → blended
  // APR + liquidity. Runs only when V2 vaults are present on the chain.
  const v2Inputs = entries
    .map(({ entry }, i) => ({ entry, i }))
    .filter(({ i }) => isV2[i])
    .map(({ entry, i }) => {
      const decimals = resolveDecimals(i)
      const underlying = entry.underlying?.toLowerCase() ?? ''
      const totalAssets = toStringSafe(phase1[i * PHASE1_PER_VAULT + 3], '0')
      const oracleKey =
        toOracleKey(tokenList[underlying]?.assetGroup) ??
        toGenericPriceKey(underlying, chainId)
      return {
        vault: entry.vault.toLowerCase(),
        underlying,
        decimals,
        totalAssetsFormatted: Number(parseRawAmount(totalAssets, decimals)),
        feePercent:
          (toNumberSafe(phase1[i * PHASE1_PER_VAULT + PERFORMANCE_FEE_INDEX]) /
            FEE_SCALE) *
          100,
        priceUsd: prices[oracleKey] ?? prices[underlying] ?? 0,
        adaptersLength: Math.max(0, Math.trunc(adapterLens[i]) || 0),
      }
    })
  const v2ByVault = await fetchV2Allocations(
    chainId,
    multicallRetry,
    v2Inputs,
    core,
    tokenList,
  )

  const out: MorphoVaults = {}
  for (let i = 0; i < entries.length; i++) {
    const { entry } = entries[i]
    const base = i * PHASE1_PER_VAULT
    const slice = phase1.slice(base, base + CALLS_PER_VAULT)
    const v2 = isV2[i]
      ? (v2ByVault.get(entry.vault.toLowerCase()) ?? null)
      : null
    const v2FeePercent = isV2[i]
      ? (toNumberSafe(phase1[base + PERFORMANCE_FEE_INDEX]) / FEE_SCALE) * 100
      : undefined
    const parsed = parseVault(
      entry,
      slice,
      chainId,
      prices,
      tokenList,
      marketIdsByVault[i],
      rateMap,
      positionMap,
      assetDecimals,
      v2,
      v2FeePercent,
    )
    if (parsed) out[parsed.address] = parsed
  }
  return out
}

/**
 * Resolve per-vault V2 allocations: adapters → their Morpho-market assets →
 * blended supply APR + real liquidity (via the shared lens `rateMap`).
 * Best-effort: missing reads degrade gracefully (a vault absent from the
 * returned map falls back to the empty-allocation path in `parseVault`).
 */
async function fetchV2Allocations(
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  v2: Array<{
    vault: string
    underlying: string
    decimals: number
    totalAssetsFormatted: number
    feePercent: number
    priceUsd: number
    adaptersLength: number
  }>,
  core: string | undefined,
  tokenList: GenericTokenList,
): Promise<
  Map<
    string,
    { depositRate: number; exposures: any[]; liquidityFormatted: number }
  >
> {
  const out = new Map<
    string,
    { depositRate: number; exposures: any[]; liquidityFormatted: number }
  >()
  if (v2.length === 0) return out

  // Round A — adapters(a) per vault (counts known from `adaptersLength`).
  const aCalls: { address: string; name: string; params: any[] }[] = []
  const aVaultIdx: number[] = []
  v2.forEach((v, vi) => {
    for (let a = 0; a < v.adaptersLength; a++) {
      aCalls.push({ address: v.vault, name: 'adapters', params: [BigInt(a)] })
      aVaultIdx.push(vi)
    }
  })
  const adaptersByVault: string[][] = v2.map(() => [])
  if (aCalls.length > 0) {
    const aRes = (await multicallRetry({
      chain: chainId,
      calls: aCalls as any,
      abi: aCalls.map(() => VAULT_V2_ABI) as any,
      allowFailure: true,
    })) as unknown[]
    aRes.forEach((r, k) => {
      if (isHex40(r)) adaptersByVault[aVaultIdx[k]].push(r.toLowerCase())
    })
  }
  const allAdapters = [...new Set(adaptersByVault.flat())]
  if (allAdapters.length === 0) return out

  // Round B — marketIdsLength() per adapter.
  const bRes = (await multicallRetry({
    chain: chainId,
    calls: allAdapters.map((a) => ({
      address: a,
      name: 'marketIdsLength',
      params: [],
    })) as any,
    abi: allAdapters.map(() => VAULT_V2_ADAPTER_ABI) as any,
    allowFailure: true,
  })) as unknown[]
  const mkLen = new Map<string, number>()
  allAdapters.forEach((a, i) => {
    const n = Number(bRes[i])
    mkLen.set(a, Number.isFinite(n) && n > 0 ? n : 0)
  })

  // Round C — marketIds(j) per adapter.
  const cCalls: { address: string; name: string; params: any[] }[] = []
  const cAdapter: string[] = []
  for (const a of allAdapters) {
    for (let j = 0; j < (mkLen.get(a) ?? 0); j++) {
      cCalls.push({ address: a, name: 'marketIds', params: [BigInt(j)] })
      cAdapter.push(a)
    }
  }
  const marketIdsByAdapter = new Map<string, string[]>(
    allAdapters.map((a) => [a, []]),
  )
  if (cCalls.length > 0) {
    const cRes = (await multicallRetry({
      chain: chainId,
      calls: cCalls as any,
      abi: cCalls.map(() => VAULT_V2_ADAPTER_ABI) as any,
      allowFailure: true,
    })) as unknown[]
    cRes.forEach((r, k) => {
      if (isHex64(r)) marketIdsByAdapter.get(cAdapter[k])!.push(r.toLowerCase())
    })
  }

  // Round D — expectedSupplyAssets(marketId) per (adapter, market).
  const dCalls: { address: string; name: string; params: any[] }[] = []
  const dMeta: { adapter: string; mid: string }[] = []
  for (const a of allAdapters) {
    for (const mid of marketIdsByAdapter.get(a) ?? []) {
      dCalls.push({ address: a, name: 'expectedSupplyAssets', params: [mid] })
      dMeta.push({ adapter: a, mid })
    }
  }
  const assetsByAdapter = new Map<string, Map<string, bigint>>()
  if (dCalls.length > 0) {
    const dRes = (await multicallRetry({
      chain: chainId,
      calls: dCalls as any,
      abi: dCalls.map(() => VAULT_V2_ADAPTER_ABI) as any,
      allowFailure: true,
    })) as unknown[]
    dRes.forEach((r, k) => {
      const v = toBigIntSafe(r)
      if (v === undefined) return
      const { adapter, mid } = dMeta[k]
      if (!assetsByAdapter.has(adapter)) assetsByAdapter.set(adapter, new Map())
      assetsByAdapter.get(adapter)!.set(mid, v)
    })
  }

  // Lens rates for every V2 market (supply APR + liquidity + collateral).
  const allMids = [...new Set([...marketIdsByAdapter.values()].flat())]
  const rateMap = await fetchMorphoMarketRates(
    chainId,
    core,
    allMids,
    multicallRetry,
  )

  v2.forEach((v, vi) => {
    const marketAssetsRaw = new Map<string, bigint>()
    for (const a of adaptersByVault[vi]) {
      const m = assetsByAdapter.get(a)
      if (!m) continue
      for (const [mid, amt] of m) {
        marketAssetsRaw.set(mid, (marketAssetsRaw.get(mid) ?? 0n) + amt)
      }
    }
    out.set(
      v.vault,
      computeV2Allocation(
        v.decimals,
        v.totalAssetsFormatted,
        v.feePercent,
        v.priceUsd,
        marketAssetsRaw,
        rateMap,
        tokenList,
        { chainId, underlying: v.underlying, protocol: 'MORPHO_BLUE' },
      ),
    )
  })
  return out
}

/**
 * Decode the Morpho lens output for `marketIds` into per-market supply APR
 * and share/asset totals. Mirrors the lending markets path: utilization →
 * AdaptiveCurve borrow APY → supply APY (net of market fee) → APR. Returns an
 * empty map when the lens or Morpho core is unavailable, so callers degrade to
 * APR `0`.
 */
async function fetchMorphoMarketRates(
  chainId: string,
  core: string | undefined,
  marketIds: string[],
  multicallRetry: MulticallRetryFunction,
): Promise<Map<string, MarketRate>> {
  const map = new Map<string, MarketRate>()
  const lens = MORPHO_LENS[chainId]
  if (!core || !lens || marketIds.length === 0) return map

  const groups = chunk(marketIds, MARKET_CHUNK)
  const calls = groups.map((group) => ({
    address: lens,
    name: 'getMarketDataCompact',
    params: [core, group],
  }))
  const raw = await multicallRetry({
    chain: chainId,
    calls: calls as any,
    abi: calls.map(() => MORPHO_LENS_ABI) as any,
    allowFailure: true,
  })

  groups.forEach((group, c) => {
    const bytes = raw[c]
    if (!bytes || bytes === '0x') return
    let decoded: Market[]
    try {
      decoded = decodeMarkets(bytes as string) as Market[]
    } catch {
      return
    }
    decoded.forEach((mkt, j) => {
      const id = group[j]?.toLowerCase()
      if (!id) return
      const utilization = MathLib.getUtilization({
        totalSupplyAssets: mkt.totalSupplyAssets,
        totalBorrowAssets: mkt.totalBorrowAssets,
      })
      let supplyApy = 0n
      try {
        const borrowApy = MathLib.getBorrowApy(
          mkt.rateAtTarget,
          utilization,
          undefined,
          Number(mkt.lastUpdate),
        )
        supplyApy = MathLib.getSupplyApy(borrowApy, utilization, mkt.fee)
      } catch {}
      const liquidity =
        mkt.totalSupplyAssets > mkt.totalBorrowAssets
          ? mkt.totalSupplyAssets - mkt.totalBorrowAssets
          : 0n
      map.set(id, {
        supplyApr: apyToApr(Number(formatEther(supplyApy))) * 100,
        totalSupplyAssets: mkt.totalSupplyAssets,
        totalSupplyShares: mkt.totalSupplyShares,
        collateralAddress: (mkt.collateralToken ?? '').toLowerCase(),
        liquidity,
      })
    })
  })
  return map
}

function parseVault(
  entry: MorphoTypeVaultEntry,
  results: any[],
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
  marketIds: string[],
  rateMap: Map<string, MarketRate>,
  positionMap: Map<string, bigint>,
  assetDecimals: Map<string, number>,
  /** Precomputed Vaults-V2 allocation, when this is a V2 vault. */
  v2Allocation?: {
    depositRate: number
    exposures: any[]
    liquidityFormatted: number
  } | null,
  /** V2 performance fee (%) — V2 vaults revert on `fee()`. */
  v2FeePercent?: number,
): MorphoVault | null {
  const address = entry?.vault?.toLowerCase()
  const underlying = entry?.underlying?.toLowerCase()
  if (!address || !underlying) return null

  const [
    nameRaw,
    symbolRaw,
    decimalsRaw,
    totalAssetsRaw,
    totalSupplyRaw,
    feeRaw,
    timelockRaw,
    ownerRaw,
    curatorRaw,
    guardianRaw,
  ] = results

  const assetMeta = tokenList[underlying]
  // `totalAssets` is in *underlying* units, so format with the asset decimals
  // (read on-chain), NOT the vault's share `decimals()` (`decimalsRaw`, often
  // 18 via MetaMorpho's decimals offset). Matches the Morpho API path, which
  // reports `asset.decimals`. Falls back to the share decimal only when the
  // on-chain read and token list both miss.
  const decimals =
    assetDecimals.get(underlying) ??
    assetMeta?.decimals ??
    Number(decimalsRaw ?? 18)

  const totalAssets = toStringSafe(totalAssetsRaw, '0')
  const totalSupply = toStringSafe(totalSupplyRaw, '0')
  const totalAssetsFormatted = Number(parseRawAmount(totalAssets, decimals))
  // Share price scales by the *share* decimals (`decimalsRaw`, 18 for
  // MetaMorpho), not the asset decimals used for `totalAssets`.
  const convertToAssets = deriveConvertToAssets(
    totalAssets,
    totalSupply,
    Number(decimalsRaw ?? 18),
  )

  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(underlying, chainId)
  const priceUsd = prices[oracleKey] ?? prices[underlying] ?? 0

  const onchainName = toStringSafe(nameRaw).trim()
  const fallbackName = onchainName || (entry.name ?? '').trim()
  const symbol = toStringSafe(symbolRaw)
  // The registry is the only curator-NAME source on these chains — the chain
  // itself yields only the curator address, and the Morpho API (which names
  // curators everywhere else) does not index them.
  const curatorName = (entry.curatorName ?? '').trim() || undefined

  // `fee()` is 1e18-scaled; `timelock()` is seconds. V2 vaults revert on
  // `fee()` — use the V2 `performanceFee` passed in instead.
  const fee =
    v2FeePercent !== undefined
      ? v2FeePercent
      : (toNumberSafe(feeRaw) / FEE_SCALE) * 100
  const timelock = toNumberSafe(timelockRaw)

  // Allocation → supply APR (net of vault fee) + per-market exposures +
  // withdrawable liquidity. V2 vaults use the precomputed adapter walk; V1
  // vaults walk the withdraw queue here. Either yields 0 / idle-only when no
  // lens or core is available for the chain.
  const { depositRate, exposures, liquidityFormatted } =
    v2Allocation ??
    computeVaultAllocation(
      address,
      decimals,
      totalAssetsFormatted,
      fee,
      priceUsd,
      marketIds,
      rateMap,
      positionMap,
      tokenList,
      { chainId, underlying, protocol: 'MORPHO_BLUE' },
    )

  return {
    address,
    underlying,
    symbol,
    name: fallbackName,
    displayName: composeVaultDisplayName(
      'Morpho',
      curatorName,
      assetMeta,
      fallbackName,
    ),
    curatorName,
    decimals,
    totalAssets,
    totalSupply,
    convertToAssets,
    supplyRate: depositRate,
    rewardsRate: 0,
    depositRate,
    exposures,
    fee,
    timelock,
    whitelisted: true,
    owner: lcOrUndefined(ownerRaw),
    curator: lcOrUndefined(curatorRaw),
    guardian: lcOrUndefined(guardianRaw),
    asset: assetMeta,
    priceUsd: priceUsd || undefined,
    totalAssetsFormatted,
    totalAssetsUsd: totalAssetsFormatted * priceUsd,
    // Real withdrawable from the allocation walk: idle + Σ min(allocation,
    // market liquidity) across the vault's markets. Degrades to `totalAssets`
    // only when no lens/core is available (rateMap empty). Fully-liquid case
    // reuses the exact raw total to avoid float round-trip drift.
    liquidity:
      liquidityFormatted >= totalAssetsFormatted
        ? totalAssets
        : toRawAmount(liquidityFormatted, decimals),
    liquidityFormatted,
    liquidityUsd: liquidityFormatted * priceUsd,
  }
}
